import {
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ChangeDetectorRef,
    Optional,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AppDialogWrapperComponent } from 'src/app/ni-components/app-dialog-wrapper/app-dialog-wrapper.component';
import { AppDialogWrapperService } from 'src/app/ni-components/app-dialog-wrapper/app-dialog-wrapper.service';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { Subscription } from 'rxjs';
import { GridBase, csvFileOptions } from 'src/app/services/tables/GridBase';
import { ColumnType } from 'src/app/shared/components/table-settings/table-settings.component';
import { FuelreqsService } from 'src/app/services/fuelreqs.service';
import { SharedService } from 'src/app/layouts/shared-service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Router } from '@angular/router';
import { localStorageAccessConstant } from 'src/app/constants/LocalStorageAccessConstant';
import { SelectedDateFilter, PresetDateFilterEnum } from 'src/app/shared/components/preset-date-filter/preset-date-filter.component';
import moment from 'moment';
import { ReportFilterItems } from 'src/app/pages/analytics/analytics-report-popup/report-filters/report-filters.component';
import { GroupCustomerCaptureRateFboResult } from 'src/app/models/group-customer-capture-rate-fbo-result';
import { CaptureRow, FboColumnInfo } from './group-customer-capture-rate.types';
import { COLUMN_IDS, COLUMN_PREFIXES, SORT_PROPERTIES } from './group-customer-capture-rate.constants';
import { CaptureRateDataTransformer, ColumnBuilder, SortHelper } from './group-customer-capture-rate.utils';
import { TableSettingsComponent } from 'src/app/shared/components/table-settings/table-settings.component';

@Component({
    selector: 'app-group-customer-capture-rate',
    templateUrl: './group-customer-capture-rate.component.html',
    styleUrls: ['./group-customer-capture-rate.component.scss'],
})
export class GroupCustomerCaptureRateComponent
    extends GridBase
    implements OnInit, OnDestroy
{
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    groupId: number;
    icao: string;
    groupedData: GroupCustomerCaptureRateFboResult[] = [];
    fboColumns: FboColumnInfo[] = [];
    reportHiddenItems: ReportFilterItems[] = [ReportFilterItems.icaoDropDown, ReportFilterItems.isCommercialAircraft];

    columns: ColumnType[] = [{ id: COLUMN_IDS.COMPANY, name: 'Company', index: 0 }];

    csvFileOptions: csvFileOptions = {
        fileName: 'Group Customer Capture Rate',
        sheetName: 'Group Customer Capture Rate',
    };

    chartName = 'group-customer-capture-rate-table';
    tableLocalStorageKey: string;

    sortChangeSubscription: Subscription;

    dialogRef: MatDialogRef<AppDialogWrapperComponent> | null = null;

    constructor(
        private fuelreqsService: FuelreqsService,
        private sharedService: SharedService,
        private ngxLoader: NgxUiLoaderService,
        @Optional() private injectedDialogRef: MatDialogRef<AppDialogWrapperComponent>,
        private cdr: ChangeDetectorRef,
        private appDialogWrapperService: AppDialogWrapperService,
        private router: Router
    ) {
        super();
        
        // Override default date filter to 7 days instead of 1 month
        this.filterStartDate = new Date(
            moment().add(-7, 'days').format('MM/DD/YYYY')
        );
        this.filterEndDate = new Date(moment().format('MM/DD/YYYY'));
        this.selectedDateFilter = {
            offsetDate: this.filterStartDate,
            limitDate: this.filterEndDate,
            selectedFilter: PresetDateFilterEnum.sevenDays
        };
        
        this.groupId = Number(
            this.sharedService.getCurrentUserPropertyValue(
                localStorageAccessConstant.groupId
            )
        );
        this.icao = this.sharedService.getCurrentUserPropertyValue(
            localStorageAccessConstant.icao
        );
        this.tableLocalStorageKey = `analytics-group-customer-capture-rate-${this.groupId}`;
        this.initColumns();
        
        // Use injected dialogRef if available (when opened via wrapper)
        if (this.injectedDialogRef) {
            this.dialogRef = this.injectedDialogRef;
        }
    }

    ngOnInit(): void {
        // Setup sorting data accessor first
        this.setupSortingDataAccessor();
        
        this.sortChangeSubscription = this.sort.sortChange.subscribe(() => {
            this.columns = this.columns.map((column) =>
                column.id === this.sort.active
                    ? { ...column, sort: this.sort.direction }
                    : {
                          hidden: column.hidden,
                          id: column.id,
                          name: column.name,
                      }
            );
            this.saveSettings(this.tableLocalStorageKey, this.columns);
        });

        this.loadData();
    }

    ngOnDestroy(): void {
        this.sortChangeSubscription?.unsubscribe();
    }

    initColumns(): void {
        this.columns = this.getClientSavedColumns(
            this.tableLocalStorageKey,
            this.columns
        );
    }

    close(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }

    private loadData(): void {
        this.ngxLoader.startLoader(this.chartName);

        this.fuelreqsService
            .getCustomerCaptureRateByGroup(
                this.groupId,
                this.filterStartDate,
                this.filterEndDate
            )
            .subscribe({
                next: (groupedData: GroupCustomerCaptureRateFboResult[]) => {
                    this.handleDataLoaded(groupedData);
                },
                error: (error: any) => {
                    console.error('Error loading customer capture rate data:', error);
                    this.ngxLoader.stopLoader(this.chartName);
                },
                complete: () => {
                    this.ngxLoader.stopLoader(this.chartName);
                },
            });
    }

    private handleDataLoaded(groupedData: GroupCustomerCaptureRateFboResult[]): void {
        this.groupedData = groupedData || [];
        this.buildColumns();
        const combinedData = CaptureRateDataTransformer.combineGroupedData(groupedData);
        
        // Ensure sorting data accessor is set up
        this.setupSortingDataAccessor();
        
        this.setVirtualScrollVariables(
            this.paginator,
            this.sort,
            combinedData
        );
        this.applyDefaultOrderingForCsvExport();
        this.cdr.detectChanges();
    }

    private buildColumns(): void {
        this.fboColumns = [];
        const baseColumns: ColumnType[] = this.createBaseColumns();

        let columnIndex = 1;
        for (const fboResult of this.groupedData) {
            const { airportOrdersColumnId, fboOrdersColumnId } =
                this.createFboColumns(fboResult, columnIndex, baseColumns);
            columnIndex += 2;
        }

        // Add Total Network Capture Rate column before Network Capture Rate
        this.addTotalNetworkCaptureRateColumn(baseColumns, columnIndex);
        columnIndex += 1;

        this.addNetworkCaptureRateColumn(baseColumns, columnIndex);
        this.columns = this.getClientSavedColumns(
            this.tableLocalStorageKey,
            baseColumns
        );
    }

    private createBaseColumns(): ColumnType[] {
        return [{ id: COLUMN_IDS.COMPANY, name: 'Company', index: 0 }];
    }

    private createFboColumns(
        fboResult: GroupCustomerCaptureRateFboResult,
        columnIndex: number,
        baseColumns: ColumnType[]
    ): { airportOrdersColumnId: string; fboOrdersColumnId: string } {
        const airportOrdersColumnId = ColumnBuilder.createAirportOrdersColumnId(
            fboResult.fboId
        );
        const fboOrdersColumnId = ColumnBuilder.createFboOrdersColumnId(
            fboResult.fboId
        );

        this.fboColumns.push({
            fboId: fboResult.fboId,
            fboName: fboResult.fboName,
            icao: fboResult.icao,
            airportOrdersColumnId,
            fboOrdersColumnId,
        });

        baseColumns.push({
            id: airportOrdersColumnId,
            name: `Total Orders at ${fboResult.icao}`,
            index: columnIndex,
            hidden: false,
        });

        baseColumns.push({
            id: fboOrdersColumnId,
            name: `${fboResult.fboName} ${fboResult.icao}`,
            index: columnIndex + 1,
            hidden: false,
        });

        return { airportOrdersColumnId, fboOrdersColumnId };
    }

    private addTotalNetworkCaptureRateColumn(
        baseColumns: ColumnType[],
        columnIndex: number
    ): void {
        baseColumns.push({
            id: COLUMN_IDS.TOTAL_NETWORK_CAPTURE_RATE,
            name: 'Total Network Capture Rate',
            index: columnIndex,
            hidden: false,
        });
    }

    private addNetworkCaptureRateColumn(
        baseColumns: ColumnType[],
        columnIndex: number
    ): void {
        baseColumns.push({
            id: COLUMN_IDS.NETWORK_CAPTURE_RATE,
            name: 'Network Capture Rate',
            index: columnIndex,
            hidden: false,
        });
    }

    private setupSortingDataAccessor(): void {
        this.dataSource.sortingDataAccessor = (item: CaptureRow, property: string) => {
            // Handle nested property paths (for backward compatibility)
            if (property.includes('.') && !property.startsWith(COLUMN_PREFIXES.AIRPORT_ORDERS) && !property.startsWith(COLUMN_PREFIXES.FBO_ORDERS)) {
                const properties = property.split('.');
                let value: any = item;
                for (const prop of properties) {
                    if (value && typeof value === 'object') {
                        value = value[prop];
                    } else {
                        return null;
                    }
                }
                return typeof value === 'string' ? value.toLocaleLowerCase() : (value ?? null);
            }
            
            // Handle direct column IDs
            switch (property) {
                case COLUMN_IDS.COMPANY:
                    return item.company ? item.company.toLocaleLowerCase() : '';
                
                case COLUMN_IDS.NETWORK_CAPTURE_RATE:
                    return item.networkCaptureRate ?? 0;
                
                case COLUMN_IDS.TOTAL_NETWORK_CAPTURE_RATE:
                    // Calculate the ratio for sorting
                    if (item.totalOrdersAcrossAllLocations === 0) {
                        return 0;
                    }
                    return item.totalOrdersInNetwork / item.totalOrdersAcrossAllLocations;
                
                default:
                    // Handle FBO airport orders columns (format: 'airportOrders_123')
                    if (property.startsWith(COLUMN_PREFIXES.AIRPORT_ORDERS)) {
                        const fboIdStr = property.replace(COLUMN_PREFIXES.AIRPORT_ORDERS, '');
                        const fboId = parseInt(fboIdStr, 10);
                        if (!isNaN(fboId)) {
                            return item.fboOrders?.[fboId]?.airportOrders ?? 0;
                        }
                    }
                    
                    // Handle FBO orders columns (format: 'fboOrders_123')
                    if (property.startsWith(COLUMN_PREFIXES.FBO_ORDERS)) {
                        const fboIdStr = property.replace(COLUMN_PREFIXES.FBO_ORDERS, '');
                        const fboId = parseInt(fboIdStr, 10);
                        if (!isNaN(fboId)) {
                            return item.fboOrders?.[fboId]?.totalOrders ?? 0;
                        }
                    }
                    
                    // Fallback for any other properties
                    const directValue = item[property as keyof CaptureRow];
                    if (directValue !== undefined) {
                        return typeof directValue === 'string' 
                            ? directValue.toLocaleLowerCase() 
                            : directValue;
                    }
                    
                    return null;
            }
        };
    }

    getFboOrders(row: CaptureRow, fboId: number): number {
        return row.fboOrders[fboId]?.totalOrders || 0;
    }

    getAirportOrders(row: CaptureRow, fboId: number): number {
        return row.fboOrders[fboId]?.airportOrders || 0;
    }

    getTotalNetworkCaptureRate(row: CaptureRow): string {
        if (row.totalOrdersAcrossAllLocations === 0) {
            return '0/0';
        }
        return `${row.totalOrdersInNetwork}/${row.totalOrdersAcrossAllLocations}`;
    }

    goToCustomerDetails(info: CaptureRow) {
        if (info.oid > 0) {
            this.router.navigate(['/default-layout/customers', info.oid]);
        }
    }

    applyPresetDateFilter(filter: SelectedDateFilter): void {
        this.filterEndDate = filter.limitDate;
        this.filterStartDate = filter.offsetDate;
        this.loadData();
    }

    /**
     * Prepares data for export by flattening the nested structure
     * @returns Array of flattened data objects ready for export
     */
    prepareExportData(): any[] {
        return this.dataSource.filteredData.map((item: CaptureRow) => {
            const flatItem: any = {
                [COLUMN_IDS.COMPANY]: item.company || '',
                [COLUMN_IDS.TOTAL_NETWORK_CAPTURE_RATE]: this.getTotalNetworkCaptureRate(item),
                [COLUMN_IDS.NETWORK_CAPTURE_RATE]: item.networkCaptureRate != null ? `${item.networkCaptureRate}%` : '0%',
            };
            
            // Flatten dynamic FBO columns
            for (const fboCol of this.fboColumns) {
                const airportOrdersValue = item.fboOrders?.[fboCol.fboId]?.airportOrders;
                const fboOrdersValue = item.fboOrders?.[fboCol.fboId]?.totalOrders;
                
                flatItem[fboCol.airportOrdersColumnId] = airportOrdersValue != null ? airportOrdersValue : 0;
                flatItem[fboCol.fboOrdersColumnId] = fboOrdersValue != null ? fboOrdersValue : 0;
            }
            
            return flatItem;
        });
    }

    exportCsv(): void {
        this.applyDefaultOrderingForCsvExport();
        
        // Prepare data for export
        const preparedData = this.prepareExportData();
        
        // Temporarily replace dataSource data with flattened data
        const originalData = this.dataSource.data;
        this.dataSource.data = preparedData;
        
        // Export with no compute function since data is already flattened
        this.exportCsvFile(
            this.columns,
            this.csvFileOptions.fileName,
            this.csvFileOptions.sheetName,
            null
        );
        
        // Restore original data
        this.dataSource.data = originalData;
    }

    applyDefaultOrderingForCsvExport(): void {
        const data = this.dataSource.data.slice();
        
        if (!this.sort.active || this.sort.direction === '') {
            this.dataSource.data = data;
            return;
        }

        this.dataSource.data = data.sort((a, b) =>
            this.sortDataRows(a, b, this.sort.direction)
        );
    }

    private sortDataRows(a: CaptureRow, b: CaptureRow, direction: string): number {
        const primaryComparison = this.compareByProperty(
            a,
            b,
            SORT_PROPERTIES.PRIMARY,
            direction
        );

        if (primaryComparison !== 0) {
            return primaryComparison;
        }

        return this.compareByProperty(
            a,
            b,
            SORT_PROPERTIES.SECONDARY,
            direction
        );
    }

    private compareByProperty(
        a: CaptureRow,
        b: CaptureRow,
        property: string,
        direction: string
    ): number {
        const valueA = SortHelper.getPropertyValue(a, property);
        const valueB = SortHelper.getPropertyValue(b, property);
        return SortHelper.compareValues(valueA, valueB, direction);
    }

    get visibleColumns(): string[] {
        const cols: string[] = [COLUMN_IDS.COMPANY];
        
        this.addFboColumnsToVisible(cols);
        this.addTotalNetworkCaptureRateColumnToVisible(cols);
        this.addNetworkCaptureRateColumnToVisible(cols);
        
        return cols;
    }

    private addFboColumnsToVisible(cols: string[]): void {
        for (const fboCol of this.fboColumns) {
            if (this.isColumnVisible(fboCol.airportOrdersColumnId)) {
                cols.push(fboCol.airportOrdersColumnId);
            }
            if (this.isColumnVisible(fboCol.fboOrdersColumnId)) {
                cols.push(fboCol.fboOrdersColumnId);
            }
        }
    }

    private addTotalNetworkCaptureRateColumnToVisible(cols: string[]): void {
        if (this.isColumnVisible(COLUMN_IDS.TOTAL_NETWORK_CAPTURE_RATE)) {
            cols.push(COLUMN_IDS.TOTAL_NETWORK_CAPTURE_RATE);
        }
    }

    private addNetworkCaptureRateColumnToVisible(cols: string[]): void {
        if (this.isColumnVisible(COLUMN_IDS.NETWORK_CAPTURE_RATE)) {
            cols.push(COLUMN_IDS.NETWORK_CAPTURE_RATE);
        }
    }

    private isColumnVisible(columnId: string): boolean {
        const column = this.columns.find((c) => c.id === columnId);
        return !column?.hidden;
    }

    trackByFboId(index: number, fboCol: FboColumnInfo): number {
        return fboCol.fboId;
    }

    openSettings(): void {
        const dialogWrapper = this.appDialogWrapperService.open<ColumnType[]>({
            title: 'Table Settings',
            component: TableSettingsComponent,
            data: [...this.columns]
        });

        dialogWrapper.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.columns.length = 0;
            this.columns.push(...result);
            
            this.saveSettings(this.tableLocalStorageKey, this.columns);
            this.cdr.detectChanges();
        });
    }
}

